import React, { Component } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Container,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  FitnessCenter as FitnessCenterIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../../assets/images/varsityFile.png'

// Higher-order component to provide hooks to class component
const withHooks = (Component) => {
  return (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    return <Component {...props} theme={theme} isMobile={isMobile} location={location} />;
  };
};

class Header extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mobileMenuOpen: false,
      scrolled: false
    };
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = () => {
    const scrolled = window.scrollY > 50;
    if (scrolled !== this.state.scrolled) {
      this.setState({ scrolled });
    }
  };

  toggleMobileMenu = () => {
    this.setState(prevState => ({
      mobileMenuOpen: !prevState.mobileMenuOpen
    }));
  };

  closeMobileMenu = () => {
    this.setState({ mobileMenuOpen: false });
  };

  render() {
    const { isMobile, location } = this.props;
    const { mobileMenuOpen, scrolled } = this.state;

    const navigationItems = [
      { label: 'Home', path: '/' },
      { label: 'Trainers', path: '/trainers' },
      { label: 'Memberships', path: '/memberships' },
      { label: 'About', path: '/about' },
      { label: 'Contact', path: '/contact' },
      { label: 'Career', path: '/career' },
    ];

    const isActive = (path) => location.pathname === path;

    return (
      <>
        <AppBar 
          position="fixed" 
          sx={{
            backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ justifyContent: 'space-between', py: 0 }}>
              {/* Logo and Navigation */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Link to="/">
                  <img
                    src={Logo}
                    alt="Varsity Fitness Logo"
                    style={{
                      height: '55px',
                      width: 'auto',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s ease',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                  />
                </Link>

                {/* Desktop Navigation */}
                {!isMobile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {navigationItems.map((item) => (
                      <Button
                        key={item.path}
                        component={Link}
                        to={item.path}
                        sx={{
                          color: isActive(item.path) ? 'primary.main' : 'text.primary',
                          fontWeight: isActive(item.path) ? 600 : 500,
                          fontSize: '1.3rem',
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          position: 'relative',
                          '&:hover': {
                            backgroundColor: 'rgba(178, 35, 53, 0.1)',
                            color: 'primary.main'
                          },
                          '&::after': isActive(item.path) ? {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '60%',
                            height: '2px',
                            backgroundColor: 'primary.main',
                            borderRadius: '1px'
                          } : {}
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Right Side - CTA Buttons and Mobile Menu */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Call to Action Buttons */}
                {!isMobile && (
                  <>
                    <Button
                      variant="contained"
                      sx={{
                        borderRadius: '25px',
                        px: 2,
                        py: 1,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '0.85rem'
                      }}
                      href="https://varsity-fitness.exercise.com/packages/61874/purchase/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join Varsity
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        borderRadius: '25px',
                        px: 2,
                        py: 1,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '0.85rem'
                      }}
                      href="https://strength.varsityfitnesslb.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Login
                    </Button>
                  </>
                )}

                {/* Mobile Menu Button */}
                {isMobile && (
                  <IconButton
                    color="primary"
                    aria-label="menu"
                    onClick={this.toggleMobileMenu}
                    sx={{ p: 1 }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={this.closeMobileMenu}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              backgroundColor: 'background.paper',
              pt: 2
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            <IconButton onClick={this.closeMobileMenu} color="primary">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <List sx={{ px: 2 }}>
            {navigationItems.map((item) => (
              <ListItem
                key={item.path}
                component={Link}
                to={item.path}
                onClick={this.closeMobileMenu}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  backgroundColor: isActive(item.path) ? 'rgba(178, 35, 53, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(178, 35, 53, 0.1)'
                  }
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: isActive(item.path) ? 600 : 500,
                      color: isActive(item.path) ? 'primary.main' : 'text.primary',
                      fontSize: '1.1rem'
                    }
                  }}
                />
              </ListItem>
            ))}
            
            <ListItem sx={{ mt: 1 }}>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: '25px',
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
                href="https://varsity-fitness.exercise.com/packages/61874/purchase/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Membership
              </Button>
            </ListItem>
            <ListItem sx={{ mt: 1 }}>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: '25px',
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
                href="https://strength.varsityfitnesslb.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Login
              </Button>
            </ListItem>
          </List>
        </Drawer>

        {/* Spacer for fixed header */}
        <Toolbar />
      </>
    );
  }
}

export default withHooks(Header);
